// Hub "Le Serie" — contenitore omnicomprensivo delle serie del sito.
// IT: Lab quant + Quant Lab (tool) + Esame OCF (sottosezione, URL invariati /it/ocf).
// EN: Lab quant + Quant Lab (tool). L'OCF resta solo-italiano.
import React, { useContext } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import { LangContext } from "../lang/LangProvider";
import { LAB_EPISODES } from "../data/labEpisodes";
import "./Series.css";

function L(lang, it, en) { return lang === "en" ? en : it; }

export default function SeriesHub() {
  const { lang } = useContext(LangContext);
  const ready = LAB_EPISODES.filter((e) => e.slug).length;

  const seoTitle = L(lang, "Le Serie — percorsi tematici", "Series — thematic tracks");
  const seoDesc = L(
    lang,
    "Le serie di AlphaFrame Analytics: il ripasso per l'Esame OCF, gli strumenti derivati dalle basi al pricing, l'aggiornamento mensile sui mercati, i modelli quantitativi raccontati con metodo (AlphaFrame Lab) e gli strumenti finanziari del sito.",
    "AlphaFrame Analytics series: derivatives from the basics to pricing, the monthly markets update, quantitative models explained with method (AlphaFrame Lab) and the site's financial tools."
  );

  return (
    <>
      <Seo title={seoTitle} description={seoDesc} path={`/${lang}/series`} image="/og/cover-tools.png" />
      <div className="series-hub">
        <header className="series-head">
          <h1>{L(lang, "Le Serie", "Series")}</h1>
          <p className="series-intro">
            {L(
              lang,
              "Percorsi tematici a puntate: ogni serie unisce video, articoli e strumenti del sito. Contenuto educativo, non consulenza personalizzata né raccomandazione.",
              "Thematic multi-episode tracks: each series combines videos, articles and the site's tools. Educational content, not personalised advice or a recommendation."
            )}
          </p>
        </header>

        {/* Due gruppi che dicono lo stato reale: serie già pubblicate come video su YouTube
            (OCF, Derivati, Mercati) e serie ancora solo testo/strumenti (Lab, Quant Lab).
            Allineamento YT↔sito 26/09/2026. */}
        <h2 className="series-group-title">{L(lang, "Serie video pubblicate", "Published video series")}</h2>
        <div className="series-grid">
          {lang === "it" && (
            <Link className="series-card" to="/it/ocf">
              <span className="series-kicker">Esame OCF · solo italiano</span>
              <h2>Preparazione Esame OCF</h2>
              <p>
                Il ripasso per materia con video e schede: mercati e vigilanza, fiscalità, assicurazioni,
                diritto privato e commerciale, sanzioni e deontologia.
              </p>
              <span className="series-meta">23 episodi indicizzati</span>
            </Link>
          )}

          <Link className="series-card" to={`/${lang}/series/derivati`}>
            <span className="series-kicker">{L(lang, "Educazione finanziaria · derivati", "Financial education · derivatives")}</span>
            <h2>{L(lang, "Strumenti derivati", "Derivatives")}</h2>
            <p>
              {L(
                lang,
                "Dalle basi (forward, future, opzioni, swap) ai modelli di prezzo: albero binomiale, processi stocastici, Black-Scholes. Ogni episodio con la sua scheda: concetti chiave, esempio, rischi.",
                "From the basics (forwards, futures, options, swaps) to pricing models: binomial trees, stochastic processes, Black-Scholes. Each episode with its note: key concepts, example, risks."
              )}
            </p>
            <span className="series-meta">{L(lang, "Serie video IT/EN · schede per episodio", "Video series IT/EN · a note for every episode")}</span>
          </Link>

          <Link className="series-card" to={`/${lang}/kb/analisi-mercati-mensile`}>
            <span className="series-kicker">{L(lang, "Aggiornamenti · mercati", "Updates · markets")}</span>
            <h2>{L(lang, "Mercati: aggiornamento mensile", "Markets: monthly update")}</h2>
            <p>
              {L(
                lang,
                "Ogni mese una lettura dei mercati: macro, ETF, settori, obbligazioni e valute. Contenuto informativo, non una raccomandazione.",
                "Each month, a read of the markets: macro, ETFs, sectors, bonds and FX. Informational content, not a recommendation."
              )}
            </p>
            <span className="series-meta">{L(lang, "Ogni mese · IT/EN", "Monthly · IT/EN")}</span>
          </Link>
        </div>

        <h2 className="series-group-title">
          {L(lang, "Testi e strumenti", "Texts and tools")}
        </h2>
        <div className="series-grid">
          <Link className="series-card" to={`/${lang}/series/lab`}>
            <span className="series-kicker">{L(lang, "Quant · modelli", "Quant · models")}</span>
            <h2>AlphaFrame Lab</h2>
            <p>
              {L(
                lang,
                "I modelli di allocazione raccontati con metodo: universo e carattere dei motori, il gate che decide chi merita fiducia, i verdetti pre-registrati. Numeri al netto del 26%.",
                "Our allocation models explained with method: each engine's universe and character, the gate that decides who deserves trust, pre-registered verdicts. Figures net of Italian 26% tax."
              )}
            </p>
            <span className="series-meta">
              {ready}/{LAB_EPISODES.length} {L(lang, "episodi disponibili", "episodes available")}
            </span>
          </Link>

          <Link className="series-card" to={`/${lang}/tools`}>
            <span className="series-kicker">{L(lang, "Quant · strumenti", "Quant · tools")}</span>
            <h2>Quant Lab</h2>
            <p>
              {L(
                lang,
                "Gli strumenti del sito applicati a domande concrete: FIRE, interesse composto, stress test di portafoglio, Risk Lab.",
                "The site's tools applied to concrete questions: FIRE, compound interest, portfolio stress tests, Risk Lab."
              )}
            </p>
            <span className="series-meta">{L(lang, "Strumenti online", "Online tools")}</span>
          </Link>
        </div>

        <p className="series-disclaimer">
          {L(
            lang,
            "Tutte le serie hanno scopo esclusivamente educativo. Le performance passate non sono indicative di quelle future.",
            "All series are for educational purposes only. Past performance is not indicative of future results."
          )}
        </p>
      </div>
    </>
  );
}
